import React, { useMemo, useRef, useState, useEffect } from "react";
import { graphql, PageProps } from "gatsby";
import {
  SvCruiseSegmentFragment,
  SitePageContext,
  SpecialVoyageEventFragment,
  SpecialVoyageQuery
} from "types/graphql-types";
import {
  SectionContainer,
  SectionTitleBody,
  SectionTitleHeading
} from "@silversea-ssc/ui-components-react";
import { t, Trans } from "@lingui/macro";
import StickyCruiseMenu from "components/StickyCruiseMenu/StickyCruiseMenu";
import { buildCruiseName } from "src/utils/cruise";
import SectionItineraryContent from "./SectionItineraryContent";
import CruiseSegmentCard from "components/CruiseSegmentCard/CruiseSegmentCard";
import Slider, { SliderSlide } from "components/Slider";
import CruiseItinerary from "components/CruiseItinerary/CruiseItinerary";
import CruiseSegmentSummary from "components/CruiseSegmentSummary/CruiseSegmentSummary";
import SpecialVoyageInfoSummary from "components/SpecialVoyageInfoSummary/SpecialVoyageInfoSummary";
import { scrollTo } from "components/StickyCruiseMenu/utils";
import SectionShip from "../Cruise/SectionShip";
import SectionSuites from "./SectionSuites";
import ShipSuiteModal from "components/ShipFacilityModal/ShipSuiteModal";
import { CruisePriceFrom } from "types/api/suite_price";
import useCruisePrice from "src/utils/useCruisePrice";
import { DestinationColor } from "@silversea-ssc/ui-components-react";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import ItineraryMapModal from "./ItineraryMapModal";
import { legacyUrl } from "src/utils/routing/legacyUrl";
import {
  useRemoteGetExcursions,
  buildItineraryWithExcursions
} from "components/CruiseItinerary/Utils";
import OpenGraph from "components/SEO/OpenGraph";
import { LocationSpecificOffer } from "components/useLocationSpecificOffers/types";
import { useLocationSpecificOffers } from "components/useLocationSpecificOffers/useLocationSpecificOffers";
import { getCruiseSpecificRAQ } from "src/utils/requestAQuoteHelper";
import { voyageToDataLayer } from "src/utils/analytics/tracking";
import { isPresent } from "src/utils/checks";
import { SectionTitle } from "@silversea-ssc/ui-components-react";
import CruiseEventCard from "components/CruiseEventCard";
import CruiseEventModal from "./CruiseEventModal";
import { LanguageCode } from "src/utils/localization/languages/types";
import PrePostProgrammes from "components/PrePostProgrammes/PrePostProgrammes";

export type CruiseSegment = SvCruiseSegmentFragment;

interface CruiseSuiteOpenModalArgs {
  type: string;
  suiteCode: string;
  price: CruisePriceFrom;
  offers: LocationSpecificOffer[];
  raqCTAUrl?: string;
}

const indexPageTitle = {
  Grand: <Trans>ALL GRAND VOYAGES</Trans>,
  World: <Trans>ALL WORLD CRUISES</Trans>
};

const cruiseEventsContent: Record<string, JSX.Element> = {
  Grand: (
    <Trans id="grand-voyage-events-section-content">
      Silversea is proud to offer full Grand Voyage guests exclusive cultural
      experiences to complement their journey of a lifetime. Enjoy!
    </Trans>
  ),
  World: (
    <Trans id="world-cruise-events-section-content">
      Silversea is proud to offer full World Cruise guests exclusive cultural
      experiences to complement their journey of a lifetime. Enjoy!
    </Trans>
  )
};
const destinationColorId = {
  Grand: 100,
  World: 101
};

function getParentPath(pageContext: SitePageContext): string | undefined {
  const parentPaths = pageContext.parentPaths;
  const languageCode = pageContext.language?.code as LanguageCode;
  if (parentPaths && languageCode)
    return legacyUrl(parentPaths[languageCode]?.path);

  return undefined;
}

const SpecialVoyage: React.FC<
  PageProps<SpecialVoyageQuery, SitePageContext>
> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const cruise = props.data.cruise!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const cruiseData = cruise.data!;

  const parentPath = getParentPath(props.pageContext);
  const language = useLanguage();

  const stickyMenuRef = useRef<HTMLDivElement>(null);
  const itineraryRef = useRef<HTMLDivElement>(null);
  const suitesRef = useRef<HTMLDivElement>(null);
  const shipRef = useRef<HTMLDivElement>(null);
  const eventsRef = useRef<HTMLDivElement>(null);
  const prePostRef = useRef<HTMLDivElement>(null);

  const [openedModal, setOpenModal] = useState<
    CruiseSuiteOpenModalArgs | null | undefined
  >(null);
  const [openedItineraryMap, setOpenItineraryMap] = useState(false);
  const [openedCruiseEvent, setOpenCruiseEvent] = useState<
    SpecialVoyageEventFragment | undefined
  >(undefined);

  const cruiseName = buildCruiseName(cruise);
  const { data: cruisePrice } = useCruisePrice(
    cruiseData?.cruiseCode,
    props.location,
    language
  );
  const cruiseSlideshow = cruise.slideshow?.filter(isPresent);
  const cruiseEvents = cruise.events?.filter(isPresent);

  const offers = useLocationSpecificOffers(cruisePrice?.exclusiveOffers || []);
  const price = cruisePrice?.priceFrom;
  const preHotelLand = cruiseData.preHotelLand?.filter(isPresent) || [];
  const postHotelLand = cruiseData.postHotelLand?.filter(isPresent) || [];
  const hasPrePostHotel = false; // preHotelLand?.length > 0 || postHotelLand?.length > 0;
  const hasExcursions = true;

  const menuSections = [
    {
      section: hasExcursions ? t`Itinerary & Excursions` : t`Itinerary`,
      ref: itineraryRef,
      trackingName: "itinerary"
    },
    { section: t`Suites & Fares`, ref: suitesRef, trackingName: "suites" },
    { section: t`Ship`, ref: shipRef, trackingName: "ship" }
  ];

  if (cruiseEvents && cruiseEvents.length > 0)
    menuSections.push({
      section: t`cruise-events-section-title`,
      ref: eventsRef,
      trackingName: "events"
    });

  if (hasPrePostHotel) {
    menuSections.push({
      section: t`Pre-post hotels`,
      ref: prePostRef,
      trackingName: "pre-post-hotels"
    });
  }

  const validCruises =
    cruise.data?.cruises?.filter((s): s is SvCruiseSegmentFragment =>
      isPresent(s?.data?.cruiseCode)
    ) || [];

  const [activeSegment, setActiveSegment] = useState<CruiseSegment>(
    validCruises[0]
  );
  const segmentMap =
    cruise.segments?.find((x) => x?.cruise?._id === activeSegment?._id)?.map ||
    activeSegment?.data?.map;
  const activeSegmentIsFirst = validCruises.indexOf(activeSegment) == 0;
  const activeSegmentIsLast =
    validCruises.indexOf(activeSegment) == validCruises.length - 1;

  const activeItinerary = activeSegment?.data?.itinerary?.filter(isPresent);

  const portCodes = activeItinerary?.map((i) => i?.port?._id).filter(isPresent);

  const { data: excursionsData } = useRemoteGetExcursions(
    activeSegment?.data?.cruiseCode,
    portCodes
  );

  const { shorexes, landProgrammes, confirmedExcursions } = excursionsData || {
    shorexes: [],
    landProgrammes: [],
    confirmedExcursions: []
  };

  const itineraryWithExcursions = useMemo(() => {
    if (activeItinerary) {
      return buildItineraryWithExcursions(
        activeItinerary,
        shorexes,
        landProgrammes,
        confirmedExcursions
      );
    } else return [];
  }, [confirmedExcursions, activeItinerary, landProgrammes, shorexes]);

  const comboType = cruiseData.comboType;
  const cruiseSpecificRAQUrl = cruiseData?.cruiseCode
    ? getCruiseSpecificRAQ(language.code, cruiseData?.cruiseCode)
    : undefined;

  useEffect(() => {
    voyageToDataLayer({
      name: cruiseName,
      code: cruiseData?.cruiseCode,
      departure_date: cruiseData?.departureDate,
      arrival_date: cruiseData?.arrivalDate,
      days_duration: cruiseData?.days,
      ship_name: cruiseData?.ship?.name,
      price: price?.type == "price" ? price?.amount : undefined,
      sale_state: price?.type,
      departure_port: cruiseData?.departurePort?.data?.code,
      arrival_port: cruiseData?.arrivalPort?.data?.code,
      destination_id: getDestinationIdFromComboType(cruiseData.comboType),
      cruise_type: cruiseData?.comboType
    });
  }, [
    cruiseName,
    price,
    cruiseData?.cruiseCode,
    cruiseData?.departureDate,
    cruiseData?.days,
    cruiseData?.ship?.name,
    cruiseData?.arrivalDate,
    cruiseData?.departurePort?.data?.code,
    cruiseData?.arrivalPort?.data?.code,
    cruiseData.comboType
  ]);

  return (
    <React.Fragment>
      <OpenGraph
        openGraph={cruise?.metadata?.openGraph}
        excludeFromSitemap={cruise?.metadata?.excludeFromSitemap}
      />
      <DestinationColor
        destinationId={getDestinationIdFromComboType(cruiseData.comboType)}
      >
        <SpecialVoyageInfoSummary
          price={price}
          cruiseCode={cruiseData?.cruiseCode}
          shipName={cruiseData?.ship?.name}
          departureDate={cruiseData?.departureDate}
          arrivalDate={cruiseData?.arrivalDate}
          duration={cruiseData?.days}
          heroImage={cruise.heroImage}
          map={cruise.map}
          cruiseAbstract={cruise.cruiseAbstract?.localized}
          cruiseTitle={cruise.name?.localized}
          cruiseName={cruiseName}
          raqUrl={cruiseSpecificRAQUrl}
          onClickOpenItineraryMap={() => setOpenItineraryMap(true)}
          onClickSuitesAndFaresButton={() => {
            if (suitesRef?.current && stickyMenuRef?.current)
              scrollTo(suitesRef.current, stickyMenuRef.current);
          }}
          indexPage={{
            url: parentPath,
            title: comboType ? indexPageTitle[comboType] : undefined
          }}
          offers={offers}
        />
        <StickyCruiseMenu
          menuRef={stickyMenuRef}
          sectionRefs={menuSections}
          summary={{
            cruiseName: cruiseName,
            price: price,
            departureDate: cruiseData?.departureDate,
            arrivalDate: cruiseData?.arrivalDate,
            days: cruiseData?.days,
            offers: offers,
            raqUrl: cruiseSpecificRAQUrl
          }}
        >
          Upper menu
        </StickyCruiseMenu>

        <div ref={itineraryRef}>
          <SectionItineraryContent
            cruise={cruise}
            slideshow={cruiseSlideshow}
            hasExcursions={hasExcursions}
            uniqueCountriesCount={cruise.uniqueCountriesCount}
            uniquePortsCount={cruise.uniquePortsCount}
            routesCount={validCruises?.length}
          />
          <SectionContainer
            backgroundColor="neutral05"
            spacingTop="regular"
            spacingBottom="compact"
            dataContentName="cruise_segment_section"
          >
            <Slider variantColor="white">
              {validCruises?.length > 0 &&
                validCruises.map((c) => {
                  const cruiseCode = c.data?.cruiseCode;
                  if (!cruiseCode) return null;
                  return (
                    <SliderSlide key={cruiseCode}>
                      <CruiseSegmentCard
                        segment={c}
                        active={activeSegment?._id == c._id}
                        onClick={(segment) => setActiveSegment(segment)}
                      />
                    </SliderSlide>
                  );
                })}
            </Slider>
          </SectionContainer>
          {activeSegment && (
            <CruiseSegmentSummary segment={activeSegment} map={segmentMap} />
          )}
          <SectionContainer
            backgroundColor="white"
            spacingTop="regular"
            spacingBottom="regular"
          >
            {activeSegment && (
              <CruiseItinerary
                itinerary={itineraryWithExcursions}
                cruiseType={activeSegment.data?.cruiseType}
                hideShorexesOnFirstDay={activeSegmentIsFirst}
                hideShorexesOnLastDay={activeSegmentIsLast}
              />
            )}
          </SectionContainer>
        </div>
        <div ref={suitesRef}>
          {cruiseData?.ship && (
            <SectionSuites
              cruise={cruise}
              cruisePrice={cruisePrice}
              offers={offers}
              cruiseFareInclusions={cruise.cruiseFareInclusions?.localized}
              cruiseFareInclusionsFootnotes={
                cruise.cruiseFareInclusionsFootnotes?.localized
              }
              setOpenModal={setOpenModal}
            />
          )}
        </div>
        {cruiseData?.ship && (
          <div ref={shipRef}>
            <SectionShip ship={cruiseData?.ship} />
          </div>
        )}

        {cruiseEvents && cruiseEvents.length > 0 && (
          <div ref={eventsRef}>
            <SectionContainer
              spacingTop="regular"
              spacingBottom="regular"
              backgroundColor="destinationExtralight"
            >
              <SectionTitle variant="wide">
                <SectionTitleHeading>
                  <Trans id="cruise-events-section-title">Event</Trans>
                </SectionTitleHeading>
                <SectionTitleBody>
                  {comboType ? cruiseEventsContent[comboType] : ""}
                </SectionTitleBody>
              </SectionTitle>
              <Slider variantColor="white">
                {cruiseEvents.map((event) => {
                  return (
                    <SliderSlide key={event._key}>
                      <CruiseEventCard
                        cardBackgroundColor="white"
                        title={event.title?.localized || ""}
                        location={event.location?.localized || ""}
                        date={event.date}
                        image={event.cardImage}
                        onClick={() => setOpenCruiseEvent(event)}
                      />
                    </SliderSlide>
                  );
                })}
              </Slider>
            </SectionContainer>
          </div>
        )}

        {openedCruiseEvent && (
          <CruiseEventModal
            modal={{
              isOpen: !!openedCruiseEvent,
              onRequestClose: () => setOpenCruiseEvent(undefined)
            }}
            event={openedCruiseEvent}
          />
        )}

        {cruise.map && openedItineraryMap && (
          <ItineraryMapModal
            modal={{
              isOpen: !!openedItineraryMap,
              onRequestClose: () => setOpenItineraryMap(false)
            }}
            map={cruise.map}
          />
        )}
        {hasPrePostHotel && (
          <div ref={prePostRef}>
            <PrePostProgrammes
              preFallbackImage={(cruiseData.departurePort?.slideshow || [])[0]}
              postFallbackImage={(cruiseData.arrivalPort?.slideshow || [])[0]}
              preProgrammes={preHotelLand}
              postProgrammes={postHotelLand}
            />
          </div>
        )}

        {cruiseData?.ship?.fullPath && openedModal && (
          <ShipSuiteModal
            modal={{
              isOpen: !!openedModal,
              onRequestClose: () => setOpenModal(null)
            }}
            size="big"
            shipPath={cruiseData?.ship.fullPath}
            {...openedModal}
          />
        )}
      </DestinationColor>
    </React.Fragment>
  );
};

export const query = graphql`
  fragment SVCruiseSegment on SanityCruise {
    _id
    data {
      cruiseCode
      cruiseType
      departureDate
      arrivalDate
      departurePort {
        data {
          code
          name {
            localized
          }
        }
      }
      arrivalPort {
        data {
          code
          name {
            localized
          }
        }
      }

      itinerary {
        ...CruiseItinerary
      }

      map {
        ...Image
      }
    }
    fullPath
  }

  fragment SpecialVoyageEvent on SanityCruiseEvent {
    _key
    cardImage: image {
      ...Image
    }
    image {
      ...HeroResponsiveImage
    }
    date
    title {
      en
      localized
    }
    content {
      localized
    }
    location {
      localized
    }
  }

  query SpecialVoyage($id: String!) {
    cruise: sanitySpecialVoyage(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }
      name {
        localized
      }

      cruiseAbstract {
        localized
      }

      cruiseFareInclusions {
        _key
        localized
      }

      cruiseFareInclusionsFootnotes {
        localized
      }

      heroImage {
        ...HeroResponsiveImage
      }

      customBrochureLink {
        localized
      }

      map {
        ...Image
      }

      uniqueCountriesCount
      uniquePortsCount

      segments {
        cruise {
          _id
        }
        map {
          ...Image
        }
      }

      itineraryDescription {
        localized
      }

      slideshow {
        ...SlideshowWithVideo
      }

      data {
        cruiseCode
        comboType
        departureDate
        arrivalDate
        days

        departurePort {
          slideshow {
            ...SlideshowOnlyImage
          }
          data {
            code
            name {
              localized
            }
          }
        }
        arrivalPort {
          slideshow {
            ...SlideshowOnlyImage
          }
          data {
            code
            name {
              localized
            }
          }
        }

        postHotelLand {
          ...HotelOrLandProgramme
        }

        preHotelLand {
          ...HotelOrLandProgramme
        }

        ship {
          ...CruiseSectionShip
        }

        cruises {
          ...SVCruiseSegment
        }
      }
      events {
        ...SpecialVoyageEvent
      }
    }
  }
`;

export default SpecialVoyage;

function getDestinationIdFromComboType(
  comboType: string | undefined | null
): number | undefined {
  if (!comboType) return undefined;
  return destinationColorId[comboType];
}
